import { Fragment, useMemo, type CSSProperties, type ReactNode } from 'react';
import { safeHref } from './markdown-html';
import { lineViews, NO_DECORATIONS, type Part } from './rich-field';
import './markdown.css';

/** A stretch of the text drawn as something of its own, such as one of a version's changes. */
export interface TextStretch {
  readonly start: number;
  readonly end: number;
  /** Draws its text: each piece of it, where formatting or a cell divides it. */
  readonly draw: (children: ReactNode) => ReactNode;
}

/** Something drawn at a place in the text that isn't written there, such as words taken out. */
export interface TextExtra {
  readonly at: number;
  readonly node: ReactNode;
}

interface MarkdownTextProps {
  readonly text: string;
  /**
   * A version just written: its words come in one after another, as if being typed. Each knows
   * how many there are, so a long version's come faster and it's whole in the same time.
   */
  readonly cascade?: boolean;
  readonly stretches?: readonly TextStretch[];
  readonly extras?: readonly TextExtra[];
}

/** Writing in Markdown, such as a version, shown formatted as the draft is. */
export function MarkdownText({ text, cascade = false, stretches, extras }: MarkdownTextProps) {
  const views = useMemo(
    () => lineViews(text, undefined, { ...NO_DECORATIONS, ranges: stretches, extras }),
    [text, stretches, extras],
  );
  const total = useMemo(() => (cascade ? (text.match(/\S+/gu)?.length ?? 0) : 0), [cascade, text]);
  let word = 0;

  const words = (chunk: string): ReactNode => {
    if (!cascade) return chunk;
    return chunk.split(/(\s+)/u).map((piece, index) =>
      index % 2 === 1 || !piece ? (
        piece
      ) : (
        <span
          key={index}
          className="cascade-word"
          style={{ '--word': word++, '--words': total } as CSSProperties}
        >
          {piece}
        </span>
      ),
    );
  };
  const draw = (part: Part, key: number): ReactNode => {
    if (part.type === 'mark') return <span key={key}>{part.parts.map(draw)}</span>;
    if (part.type === 'cell')
      return (
        <span key={key} className="md-cell">
          {part.parts.map(draw)}
        </span>
      );
    if (part.type === 'extra') return <Fragment key={key}>{extras?.[part.index]?.node}</Fragment>;
    if (part.type === 'rest' || part.hidden) return null;
    const stretch = part.range === undefined ? undefined : stretches?.[part.range];
    const shown = stretch ? stretch.draw(words(part.text)) : words(part.text);
    const href = part.href && safeHref(part.href);
    if (href)
      return (
        <a
          key={key}
          className={part.className}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {shown}
        </a>
      );
    if (!part.className) return <span key={key}>{shown}</span>;
    return (
      <span key={key} className={part.className}>
        {shown}
      </span>
    );
  };

  return views.map((view, index) => (
    <div
      key={index}
      className={view.className}
      data-depth={
        view.kind === 'bullet' || view.kind === 'ordered' || view.kind === 'task'
          ? Math.min(view.depth, 2)
          : undefined
      }
      data-number={view.number || undefined}
      style={
        view.depth > 0 || view.columns > 0
          ? ({
              ...(view.depth > 0 && { '--depth': view.depth }),
              ...(view.columns > 0 && { '--columns': view.columns }),
            } as CSSProperties)
          : undefined
      }
    >
      {view.checked !== null && <span className="md-check is-shown" aria-hidden="true" />}
      {view.parts.map(draw)}
      {view.empty && <br />}
      {/* Its line break, unseen, so the text reads as it's written, line by line. */}
      {!view.last && <span hidden>{'\n'}</span>}
    </div>
  ));
}
